import React, { useState, useEffect, useRef, useMemo } from 'react';
import studyDesignSelectionService from '../../services/studyDesignSelection.service';
import { useProject } from '../../context/ProjectContext';
import PolicyDeniedPane from '../access/PolicyDeniedPane';
import { usePolicyStore } from '../../stores/policyStore';
import { usePolicyTabGate } from '../../hooks/policyHooks';
import {
  Box,
  Button,
  Paper,
  Container,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  CircularProgress,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PublicIcon from '@mui/icons-material/Public';
import TranslateIcon from '@mui/icons-material/Translate';
import ClinicalIntakeForm from './ClinicalIntakeForm';
import SourceDocumentsForm from './SourceDocumentsForm';
import Feasibility from './Feasibility';
import OperationInputForm from '../../sap/components/OperationInputForm';
import DocumentEditor from '../../protocol/components/DocumentEditor';
import DocumentMap from './DocumentMap';
import AdministrativeInfoForm from './AdministrativeInfoForm';
import StudyRegistrationForm from './StudyRegistrationForm';
import TemplateLanguageForm from './TemplateLanguageForm';
import UsdmTemplateTab from './UsdmTemplateTab';
import S9AssessmentScheduleForm from './S9AssessmentScheduleForm';
import { getStudyRegistrationByProject } from '../../services/studyRegistration.service';
import { studyRegistrationToAdminFields } from '../../utils/studyRegistrationToAdminFields';
import studyProtocolService from '../../services/studyProtocol.service';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ICFEditor from '../../icf/components/ICFEditor';
import SAPEditor from '../../sap/components/SAPEditor';
import SAPEditorUsdm from '../../sap/components/SAPEditorUsdm';
import INDPage from '../../ind/components/INDPage';
import clinicalIntakeService from '../../services/clinicalIntake.service';
import administrativeInfoService from '../../services/administrativeInfo.service';
import StudyDashboard from '../dashboard/StudyDashboard';
//import ProtocolRegistration from '../protocolRegistration/ProtocolRegistration';
import RegistryManagement from '../registryManagement/RegistryManagement';
import SOPsPage from '../sops/SOPsPage';
import TranslationsPage from '../translations/TranslationsPage';
//import SubmitResultsPage from '../submitResults/SubmitResultsPage';
import DesignStudioPage from '../designStudio/DesignStudioPage';
import TemplatesTab from '../templates/TemplatesTab';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { unifiedDocumentList } from '../../pages/DocumentMap';
import { IND_ENABLED } from '../../config/env';
import { getStudyOpenMode, OPEN_MODE_DESIGN_STUDIO } from '../../utils/studyOpenPreference';

// Placeholder components for new tabs (these are no longer needed as main tabs)
// const ICFEditor = () => <Box sx={{ p: 3 }}><Typography variant="h5">Informed Consent Form (ICF) Editor</Typography><Typography>Content for ICF goes here.</Typography></Box>;
// const SAPEditor = () => <Box sx={{ p: 3 }}><Typography variant="h5">Statistical Analysis Plan (SAP) Editor</Typography><Typography>Content for SAP goes here.</Typography></Box>;

const mainTabs = [
  // { label: 'Document Map', component: DocumentMap },
  { label: 'Protocol Intake Form', component: ClinicalIntakeForm },
  //{ label: 'Administrative Info', component: AdministrativeInfoForm },
  { label: 'Template Language', component: TemplateLanguageForm },
  { label: 'Document Editor', component: DocumentEditor },
  { label: 'SAP', component: null }, // New SAP tab
];

const subTabsDocumentEditor = [
  { label: 'Protocol', value: 0 },
  { label: 'ICF', value: 1 },
  { label: 'SAP', value: 2 },
];

const subTabsSAP = [
  // Hidden from the UI for now (code retained for future re-enable)
  { label: 'SAP Intake Form', value: 0, hidden: true },
  { label: 'SAP Editor', value: 1, hidden: true },
  { label: 'SAP usdm', value: 2 },
];

const visibleSubTabsSAP = subTabsSAP.filter((subTab) => !subTab.hidden);
const DEFAULT_SAP_SUB_TAB = visibleSubTabsSAP[0]?.value ?? 0;

const protocolSubTabs = [
  { label: 'Study Registration', slug: 'study-registration' },
  { label: 'Source Documents', slug: 'source-documents' },
  { label: 'Administrative Info', slug: 'administrative-info' },
  { label: 'Protocol Intake Form', slug: 'clinical-intake' },
  { label: 'SOA', slug: 'soa' },
  { label: 'Template Language', slug: 'template-language' },
  { label: 'USDM Template', slug: 'usdm-template' },
  { label: 'Document Editor', slug: 'document-editor' },
];

// Which Protocol sub-tabs are shown depends on how the study was opened (chosen once
// on the Home study card). Nothing is removed - the panels below still key off the
// original index, so this only filters which tabs are rendered.
const DESIGN_STUDIO_PROTOCOL_SLUGS = ['study-registration', 'usdm-template', 'document-editor'];

const visibleProtocolSubTabIndexes = (openMode) =>
  protocolSubTabs.reduce((acc, subTab, idx) => {
    const visible = openMode === OPEN_MODE_DESIGN_STUDIO
      ? DESIGN_STUDIO_PROTOCOL_SLUGS.includes(subTab.slug)
      : subTab.slug !== 'usdm-template'; // Document Authoring Tool: everything but USDM Template
    if (visible) acc.push(idx);
    return acc;
  }, []);

const OTHER_DOCS = [
  {
    id: 'feasibility',
    name: 'Feasibility',
    icon: <AssessmentIcon fontSize="small" />,
    requiredDoc: 'Protocol',
    requiredLabel: 'Protocol',
  },
  {
    id: 'registry-management',
    name: 'Registry Management',
    icon: <PublicIcon fontSize="small" />,
    requiredDoc: 'Protocol',
    requiredLabel: 'Protocol',
  },
  {
    id: 'translations',
    name: 'Translations',
    icon: <TranslateIcon fontSize="small" />,
    requiredDoc: 'ICF',
    requiredLabel: 'ICF',
  },
];

// Hidden entirely, whatever mode the study was opened in: not offered in Study Setup,
// not shown in the top nav (MainLayout.js), and ?tab= deep links fall back to Study
// Setup. Code retained for future re-enable.
const HIDDEN_OTHER_DOC_IDS = new Set(['feasibility', 'registry-management']);

const isOtherDocHidden = (docId) => HIDDEN_OTHER_DOC_IDS.has(docId);

const VISIBLE_OTHER_DOCS = OTHER_DOCS.filter((doc) => !isOtherDocHidden(doc.id));

/** Study Design: only these three core documents. */
const CORE_STUDY_DESIGN_DOCS = [
  { id: 'Protocol', name: 'Protocol', icon: <AssignmentIcon fontSize="small" /> },
  { id: 'ICF', name: 'ICF', icon: <AssignmentIcon fontSize="small" /> },
  { id: 'sap', name: 'SAP', icon: <AssignmentIcon fontSize="small" /> },
  // Behind REACT_APP_ENABLE_IND (see config/env.js)
  ...(IND_ENABLED ? [{ id: 'IND', name: 'IND', icon: <AssignmentIcon fontSize="small" /> }] : []),
];

const CORE_STUDY_DESIGN_IDS = new Set(CORE_STUDY_DESIGN_DOCS.map((d) => d.id));

// IND tab policy (ind-package-spec 02 §6): until the IAM Hub defines an 'ind'
// action the /policy/me snapshot has no 'ind' key at all, and the tab is treated
// as permitted. Warn once per app session so the gap stays visible in the console.
let indUngovernedWarned = false;

/** Remaining clinical docs (not Protocol/ICF/SAP) — Documents under development. */
const UNDER_DEVELOPMENT_DOCS = unifiedDocumentList.filter(
  (doc) => !CORE_STUDY_DESIGN_IDS.has(doc.id)
);

const VALID_STUDY_DESIGN_DOC_IDS = new Set([
  ...unifiedDocumentList.map((doc) => doc.id),
  ...OTHER_DOCS.map((doc) => doc.id),
  // Kept valid while IND is flagged off so a study's saved IND selection isn't
  // dropped on the next save; the tab itself is gated on IND_ENABLED.
  'IND',
]);

const sanitizeStudyDesignSelection = (docIds = []) =>
  docIds.filter((id) => VALID_STUDY_DESIGN_DOC_IDS.has(id));

const HomeStudyDesignTab = ({ selectedDocs = new Set(), onDocToggle, policiesLoaded, probeSignature }) => {
  const [innerTab, setInnerTab] = useState(0); // 0 = core, 1 = Other Documents, 2 = under development
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUnderDevDocs = UNDER_DEVELOPMENT_DOCS.filter((doc) =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Same cache-only tab-gate pattern as the global tabs (Protocol/SAP/ICF/...) in the
  // parent — one usePolicyTabGate per Study Setup inner tab, keyed on its own Hub IAM
  // action-tab attribute (clinical_documents / other_documents / documents_under_development,
  // children of study_dashboard in local_app_attribute_definitions).
  const clinicalDocsPermitted = usePolicyStore((s) => s.canTab('clinical_documents:open'));
  const clinicalDocsGate = usePolicyTabGate({
    active: innerTab === 0,
    policiesLoaded,
    cachedPermitted: clinicalDocsPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to Clinical Documents for this study.',
    probeSignature,
  });
  const otherDocsPermitted = usePolicyStore((s) => s.canTab('other_documents:open'));
  const otherDocsGate = usePolicyTabGate({
    active: innerTab === 1,
    policiesLoaded,
    cachedPermitted: otherDocsPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to Other Documents for this study.',
    probeSignature,
  });
  const underDevPermitted = usePolicyStore((s) => s.canTab('documents_under_development:open'));
  const underDevGate = usePolicyTabGate({
    active: innerTab === 2,
    policiesLoaded,
    cachedPermitted: underDevPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to Documents Under Development for this study.',
    probeSignature,
  });

  // Same revalidate-on-tab-change pattern as the parent's global tabs (globalTabMountedRef):
  // a permission edited in Hub IAM, or a resource_access update, should take effect on the
  // next inner-tab click instead of requiring a full page reload. Silent + force: skips the
  // loading flag (no spinner flash, cached content stays visible while it revalidates) but
  // bypasses fetchPolicies' "already loaded" cache short-circuit so it actually hits the
  // server. Skipped on first mount — the parent's own fetch already covers the initial load.
  const fetchPolicies = usePolicyStore((s) => s.fetchPolicies);
  const innerTabMountedRef = useRef(false);
  useEffect(() => {
    if (!probeSignature) return;
    if (!innerTabMountedRef.current) {
      innerTabMountedRef.current = true;
      return;
    }
    fetchPolicies({ studyId: probeSignature, force: true, silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerTab]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Study Setup</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select documents below to unlock their editing tabs in the top navigation.
      </Typography>

      <Tabs
        value={innerTab}
        onChange={(_, v) => setInnerTab(v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
        }}
      >
        <Tab label="Clinical Documents" value={0} />
        <Tab label="Other Documents" value={1} />
        <Tab label="Documents under development" value={2} />
      </Tabs>

      {innerTab === 0 && (
        !policiesLoaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : clinicalDocsGate.showDenied ? (
          <PolicyDeniedPane reason={clinicalDocsGate.deniedMessage} size="fill" />
        ) : (
        <Paper sx={{ p: 2 }}>
          <List disablePadding>
            {CORE_STUDY_DESIGN_DOCS.map((doc) => (
              <ListItem key={doc.id} divider disableGutters sx={{ px: 1 }}>
                <Checkbox
                  checked={selectedDocs.has(doc.id)}
                  onChange={(e) => onDocToggle && onDocToggle(doc.id, e.target.checked)}
                  size="small"
                />
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {doc.icon || <AssignmentIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText primary={doc.name} />
                {selectedDocs.has(doc.id) && (
                  <Chip label="Enabled" size="small" color="success" sx={{ ml: 1 }} />
                )}
              </ListItem>
            ))}
          </List>
        </Paper>
        )
      )}

      {innerTab === 1 && (
        !policiesLoaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : otherDocsGate.showDenied ? (
          <PolicyDeniedPane reason={otherDocsGate.deniedMessage} size="fill" />
        ) : (
        <Paper sx={{ p: 2 }}>
          <List disablePadding>
            {VISIBLE_OTHER_DOCS.map((doc) => {
              const isUnlocked = selectedDocs.has(doc.requiredDoc);
              return (
                <ListItem
                  key={doc.id}
                  divider
                  disableGutters
                  sx={{ px: 1, opacity: isUnlocked ? 1 : 0.55 }}
                >
                  <Tooltip
                    title={!isUnlocked ? `Select ${doc.requiredLabel} first to enable this` : ''}
                    placement="right"
                  >
                    <span>
                      <Checkbox
                        checked={selectedDocs.has(doc.id)}
                        onChange={(e) => onDocToggle && onDocToggle(doc.id, e.target.checked)}
                        size="small"
                        disabled={!isUnlocked}
                      />
                    </span>
                  </Tooltip>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {isUnlocked ? doc.icon : <LockIcon fontSize="small" color="disabled" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.name}
                    secondary={
                      !isUnlocked ? `Requires ${doc.requiredLabel} to be selected` : null
                    }
                    secondaryTypographyProps={{ color: 'warning.main', fontSize: '0.75rem' }}
                  />
                  {selectedDocs.has(doc.id) && (
                    <Chip label="Enabled" size="small" color="success" sx={{ ml: 1 }} />
                  )}
                </ListItem>
              );
            })}
          </List>
        </Paper>
        )
      )}

      {innerTab === 2 && (
        !policiesLoaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : underDevGate.showDenied ? (
          <PolicyDeniedPane reason={underDevGate.deniedMessage} size="fill" />
        ) : (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <TextField
              size="small"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 240 }}
            />
          </Box>
          <Paper sx={{ p: 2 }}>
            {filteredUnderDevDocs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No documents match &quot;{searchTerm}&quot;
              </Typography>
            ) : (
              <List disablePadding>
                {filteredUnderDevDocs.map((doc) => (
                  <ListItem key={doc.id} divider disableGutters sx={{ px: 1 }}>
                    <Checkbox
                      checked={selectedDocs.has(doc.id)}
                      onChange={(e) => onDocToggle && onDocToggle(doc.id, e.target.checked)}
                      size="small"
                    />
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {doc.icon || <AssignmentIcon fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText primary={doc.name} />
                    {selectedDocs.has(doc.id) && (
                      <Chip label="Enabled" size="small" color="success" sx={{ ml: 1 }} />
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Box>
        )
      )}
    </Box>
  );
};

const initialFormData = {
  study_identification: {
    study_title: '',
    protocol_number: '',
    version_number_date: '',
    sponsor_name: '',
    cro: '',
  },
  study_overview: {
    therapeutic_area: '',
    disease_indication: '',
    study_phase: '',
    study_type: '',
    randomization: false,
    blinding: '',
    primary_objective: '',
    secondary_objectives: '',
  },
  target_population: {
    patient_population_description: '',
    key_inclusion_criteria: [''],
    key_exclusion_criteria: [''],
  },
  site_operations: {
    site_activation: {
      planned_sites_count: '',
      countries_involved: '',
      site_initiation_timeline: '',
      site_activation_strategy: '',
    },
    monitoring_plan: {
      monitoring_frequency: '',
      monitoring_type: '',
      risk_based_monitoring: false,
      central_monitoring: false,
    },
  },
  document_management: {
    filing_strategy: '',
    quality_control: {
      qc_level: '',
      qc_frequency: '',
      automated_checks: false,
    },
    retention_period: '',
    archival_strategy: '',
  },
  regulatory_compliance: {
    submission_timeline: '',
    regulatory_authorities: '',
    ethics_committee_strategy: '',
    safety_reporting: {
      reporting_timeline: '',
      safety_monitoring: '',
    },
  },
  vendor_management: {
    cro_details: {
      name: '',
      scope_of_work: '',
      oversight_plan: '',
    },
    central_lab: {
      name: '',
      services: '',
    },
    other_vendors: '',
  },
};

const StudyFormTabs = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [documentSubTab, setDocumentSubTab] = useState(0);
  const [sapSubTab, setSapSubTab] = useState(DEFAULT_SAP_SUB_TAB);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // Lazy-init from the URL (not `null`) — projectIdFromUrl below is computed
  // from the exact same window.location.search, but *this* state used to
  // start at null and only get synced to it inside an effect (below) that
  // runs one tick after first render. Any Save click landing in that window
  // (e.g. right after navigating here) sent projectId: null to the server
  // ("projectId is required"), even though the URL had the correct id the
  // whole time. Reading it directly here removes that window entirely.
  const [currentProjectId, setCurrentProjectId] = useState(
    () => new URLSearchParams(window.location.search).get('project') || null
  );
  const [isViewMode, setIsViewMode] = useState(false);
  const [globalTab, setGlobalTab] = useState(0); // 0 = SAP, 1 = Main Tabs
  const [protocolSubTab, setProtocolSubTab] = useState(0);
  // Set by the USDM Template tab's "Generate Protocol" button — tells
  // Document Editor (protocolSubTab === 7) to auto-run "Generate from USDM"
  // on arrival instead of requiring a second click there.
  const [autoGenerateFromUsdm, setAutoGenerateFromUsdm] = useState(false);
  const [studyRegRefreshKey, setStudyRegRefreshKey] = useState(0);
  const [trialSchemaRevision, setTrialSchemaRevision] = useState(0);
  const [intakeRevision, setIntakeRevision] = useState(0);
  const [registryManagementSubTab, setRegistryManagementSubTab] = useState(0);


  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const formIdFromUrl = queryParams.get('formId');
  const projectIdFromUrl = queryParams.get('project');
  const viewModeFromUrl = queryParams.get('viewMode') === 'true';
  const tabFromUrl = queryParams.get('tab');
  const protocolSubTabFromUrl = queryParams.get('protocolSubTab');

  // Saved on the Home study card the first time the study is opened.
  const studyOpenMode = getStudyOpenMode(projectIdFromUrl);
  const visibleProtocolSubTabs = useMemo(
    () => visibleProtocolSubTabIndexes(studyOpenMode),
    [studyOpenMode]
  );
  // A deep link (or a previously-selected tab) can point at a sub-tab this open mode
  // hides. Keep the underlying state as-is and just highlight the first visible tab,
  // so MUI never gets a value with no matching <Tab>.
  const selectedProtocolSubTab = visibleProtocolSubTabs.includes(protocolSubTab)
    ? protocolSubTab
    : visibleProtocolSubTabs[0] ?? false;

  // Hub IAM policy gate: ensures the study behind ?project= is re-checked on direct/deep
  // links too, not just when arriving via the sidebar (which already calls loadProject
  // before navigating here). See ProjectContext.loadProject / userHasProjectAccess.
  const { currentProject: policyProject, deniedReason: projectDeniedReason, loadProject } = useProject();
  useEffect(() => {
    if (!projectIdFromUrl) return;
    if (policyProject && String(policyProject._id) === String(projectIdFromUrl)) return;
    loadProject(projectIdFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdFromUrl]);
  const projectAccessDenied =
    Boolean(projectIdFromUrl) &&
    Boolean(projectDeniedReason) &&
    (!policyProject || String(policyProject._id) !== String(projectIdFromUrl));

  // Home tab gate: GET /policy/me is cached per-study (role is resolved from the
  // resource_access row matching this specific study), so it must be re-fetched
  // whenever the study in view changes. resource_access rows are keyed on the IAM
  // resource id (policyProject.iamResourceId), NOT the local Project._id in the URL
  // (?project=) — those are different id spaces, so this must wait for the loaded
  // project to actually match the URL rather than falling back to projectIdFromUrl,
  // which would silently scope every policy check to an id resource_access never uses.
  const fetchPolicies = usePolicyStore((s) => s.fetchPolicies);
  const policiesLoaded = usePolicyStore((s) => s.loaded);
  // Policy module key renamed from 'home' to 'study_dashboard' (local_app_attribute_definitions
  // + every policy condition referencing it) to match the "Study Dashboard" tab rename.
  const homeTabPermitted = usePolicyStore((s) => s.canTab('study_dashboard'));
  const policyStudyScopeId =
    policyProject && String(policyProject._id) === String(projectIdFromUrl)
      ? policyProject.iamResourceId || ''
      : '';
  useEffect(() => {
    if (!policyStudyScopeId) return;
    fetchPolicies({ studyId: policyStudyScopeId });
  }, [policyStudyScopeId, fetchPolicies]);
  // Re-validate against the server every time the active tab changes (not just when
  // the study changes) — a permission edited in Hub IAM, or a resource_access update,
  // should take effect on the next tab click instead of requiring a full page reload.
  // Silent + force: skips the loading flag (no spinner flash, cached content stays
  // visible while it revalidates) but bypasses fetchPolicies' "already loaded" cache
  // short-circuit so it actually hits the server. Skipped on first mount — the effect
  // above already covers the initial load for this study.
  const globalTabMountedRef = useRef(false);
  useEffect(() => {
    if (!policyStudyScopeId) return;
    if (!globalTabMountedRef.current) {
      globalTabMountedRef.current = true;
      return;
    }
    fetchPolicies({ studyId: policyStudyScopeId, force: true, silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalTab]);
  const homeTabGate = usePolicyTabGate({
    active: globalTab === 0,
    policiesLoaded,
    cachedPermitted: homeTabPermitted,
    // Cache-only: StudyDashboard makes its own data calls, no dedicated home:open
    // business endpoint exists to probe realtime against (see study:open, which does).
    // Module key renamed 'home' -> 'study_dashboard' in Hub IAM (local_app_attribute_definitions
    // + every policy condition referencing it) to match this tab's rename. Study Setup was
    // already merged under this same gate before the rename (never separately gated as its
    // own module), so this one key change covers both pieces of merged content.
    skipProbe: true,
    deniedReason: 'You do not have access to the Study Dashboard tab for this study.',
    probeSignature: policyStudyScopeId,
  });

  // Same cache-only pattern as Home for the remaining global tabs. Each tab's own
  // content makes its own data calls, so there's no dedicated <module>:open business
  // endpoint to probe realtime against — gate purely on the /policy/me cache.
  const protocolTabPermitted = usePolicyStore((s) => s.canTab('protocol'));
  const protocolTabGate = usePolicyTabGate({
    active: globalTab === 1,
    policiesLoaded,
    cachedPermitted: protocolTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the Protocol tab for this study.',
    probeSignature: policyStudyScopeId,
  });
  const sapTabPermitted = usePolicyStore((s) => s.canTab('sap'));
  const sapTabGate = usePolicyTabGate({
    active: globalTab === 2,
    policiesLoaded,
    cachedPermitted: sapTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the SAP tab for this study.',
    probeSignature: policyStudyScopeId,
  });
  const icfTabPermitted = usePolicyStore((s) => s.canTab('icf'));
  const icfTabGate = usePolicyTabGate({
    active: globalTab === 3,
    policiesLoaded,
    cachedPermitted: icfTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the ICF tab for this study.',
    probeSignature: policyStudyScopeId,
  });
  const registryManagementTabPermitted = usePolicyStore((s) => s.canTab('registry_management'));
  const registryManagementTabGate = usePolicyTabGate({
    active: globalTab === 5,
    policiesLoaded,
    cachedPermitted: registryManagementTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the Registry Management tab for this study.',
    probeSignature: policyStudyScopeId,
  });
  const translationsTabPermitted = usePolicyStore((s) => s.canTab('translations'));
  const translationsTabGate = usePolicyTabGate({
    active: globalTab === 7,
    policiesLoaded,
    cachedPermitted: translationsTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the Translations tab for this study.',
    probeSignature: policyStudyScopeId,
  });
  const designStudioTabPermitted = usePolicyStore((s) => s.canTab('design_studio'));
  const designStudioTabGate = usePolicyTabGate({
    active: globalTab === 8,
    policiesLoaded,
    cachedPermitted: designStudioTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the Design Studio tab for this study.',
    probeSignature: policyStudyScopeId,
  });
  const libraryTabPermitted = usePolicyStore((s) => s.canTab('library'));
  const libraryTabGate = usePolicyTabGate({
    active: globalTab === 9,
    policiesLoaded,
    cachedPermitted: libraryTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the Library tab for this study.',
    probeSignature: policyStudyScopeId,
  });
  // IND: same cache-only gate as SAP, except an 'ind' key missing from the policy
  // snapshot means the Hub has not defined the action yet -> ungoverned, permitted.
  const policySnapshot = usePolicyStore((s) => s.policies);
  const indTabCachedPermitted = usePolicyStore((s) => s.canTab('ind'));
  const indTabGoverned = Object.prototype.hasOwnProperty.call(policySnapshot || {}, 'ind');
  const indTabPermitted = indTabGoverned ? indTabCachedPermitted : true;
  useEffect(() => {
    if (policiesLoaded && !indTabGoverned && !indUngovernedWarned) {
      indUngovernedWarned = true;
      console.warn("IND tab is ungoverned: no 'ind' action defined in policy snapshot");
    }
  }, [policiesLoaded, indTabGoverned]);
  const indTabGate = usePolicyTabGate({
    active: globalTab === 10,
    policiesLoaded,
    cachedPermitted: indTabPermitted,
    skipProbe: true,
    deniedReason: 'You do not have access to the IND tab for this study.',
    probeSignature: policyStudyScopeId,
  });

  // Tracks which documents are checked in Study Design; controls visibility of Protocol/SAP/ICF global tabs
  // Persisted in MongoDB (shared across all users) with localStorage as instant fallback
  const STORAGE_KEY = `study_selected_docs_${projectIdFromUrl || 'default'}`;
  const [selectedDocs, setSelectedDocs] = useState(() => {
    try {
      const saved = localStorage.getItem(`study_selected_docs_${new URLSearchParams(window.location.search).get('project') || 'default'}`);
      return saved ? new Set(sanitizeStudyDesignSelection(JSON.parse(saved))) : new Set();
    } catch {
      return new Set();
    }
  });
  const [docSelectionWarning, setDocSelectionWarning] = useState('');

  // Load selections from MongoDB on mount / when project changes
  useEffect(() => {
    if (!projectIdFromUrl) return;
    studyDesignSelectionService.getSelection(projectIdFromUrl)
      .then(docs => {
        const sanitized = sanitizeStudyDesignSelection(docs);
        const asSet = new Set(sanitized);
        setSelectedDocs(asSet);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized)); } catch {}
      })
      .catch(() => {
        // MongoDB unavailable — keep the localStorage value already loaded in useState
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdFromUrl]);

  const handleDocToggle = (docId, checked) => {
    // Validate dependencies before enabling
    if (checked) {
      if (docId === 'sap' && !selectedDocs.has('Protocol')) {
        setDocSelectionWarning('Please select Protocol first before enabling SAP.');
        return;
      }
      if (docId === 'ICF' && !selectedDocs.has('Protocol')) {
        setDocSelectionWarning('Please select Protocol first before enabling ICF.');
        return;
      }
      if (docId === 'IND' && !selectedDocs.has('Protocol')) {
        setDocSelectionWarning('Please select Protocol first before enabling IND.');
        return;
      }
      if (docId === 'feasibility' && !selectedDocs.has('Protocol')) {
        setDocSelectionWarning('Please select Protocol first before enabling Feasibility.');
        return;
      }
      if (docId === 'registry-management' && !selectedDocs.has('Protocol')) {
        setDocSelectionWarning('Please select Protocol first before enabling Registry Management.');
        return;
      }
      if (docId === 'translations' && !selectedDocs.has('ICF')) {
        setDocSelectionWarning('Please select ICF first before enabling Translations.');
        return;
      }
    }
    setDocSelectionWarning('');
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(docId);
      } else {
        next.delete(docId);
        // Protocol unchecked → cascade remove all dependents
        if (docId === 'Protocol') {
          next.delete('sap');
          next.delete('ICF');
          next.delete('IND');
          next.delete('feasibility');
          next.delete('registry-management');
          next.delete('translations'); // ICF removed → translations removed too
          if ([1, 2, 3, 4, 5, 7, 10].includes(globalTab)) setGlobalTab(0);
        }
        // ICF unchecked → remove translations
        if (docId === 'ICF') {
          next.delete('translations');
          if (globalTab === 3 || globalTab === 7) setGlobalTab(0);
        }
        if (docId === 'sap' && globalTab === 2) setGlobalTab(0);
        if (docId === 'IND' && globalTab === 10) setGlobalTab(0);
        if (docId === 'feasibility' && globalTab === 4) setGlobalTab(0);
        if (docId === 'registry-management' && globalTab === 5) setGlobalTab(0);
        if (docId === 'translations' && globalTab === 7) setGlobalTab(0);
      }
      const asArray = sanitizeStudyDesignSelection([...next]);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(asArray)); } catch {}
      // MainLayout.js renders the actual top-bar study tabs from its own,
      // separately-fetched copy of this same selection — the native
      // `storage` event only fires in *other* tabs/windows, never this one,
      // so without this the tab bar stayed stale until a full reload
      // remounted MainLayout and re-triggered its fetch. Broadcasting here
      // lets it update immediately instead.
      try {
        window.dispatchEvent(new CustomEvent('study-selected-docs-changed', {
          detail: { projectId: projectIdFromUrl, selectedDocs: asArray },
        }));
      } catch {}
      if (projectIdFromUrl) {
        studyDesignSelectionService.saveSelection(projectIdFromUrl, asArray)
          .catch(err => console.warn('Failed to save study design selection to server:', err));
      }
      return new Set(asArray);
    });
  };

  // Expose setActiveTab to window object
  useEffect(() => {
    window.setActiveTab = setActiveTab;
    return () => {
      delete window.setActiveTab;
    };
  }, []);

  const normalizeAdminDates = (data) => {
    if (!data) return data;
    const clone = { ...data };
    const toYMD = (value) => {
      if (!value) return '';
      try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toISOString().slice(0, 10);
      } catch {
        return value;
      }
    };
    if (clone.version_date) {
      clone.version_date = toYMD(clone.version_date);
    }
    if (clone.sponsor_approval_date) {
      clone.sponsor_approval_date = toYMD(clone.sponsor_approval_date);
    }
    return clone;
  };

  const ADMIN_AUTO_POPULATE_OMIT = new Set([
    'version_number',
    'version_date',
    'official_title',
    'short_title',
    'trial_acronym',
    'original_protocol_indicator',
    'amendment_identifier',
    'amendment_scope',
    'status',
  ]);

  const handleAutoPopulateResult = (payload) => {
    if (!payload || typeof payload !== 'object') return;

    setFormData((prev) => {
      let next = { ...prev };

      if (payload.adminData && typeof payload.adminData === 'object') {
        const {
          _id,
          project,
          __v,
          createdAt,
          updatedAt,
          submittedAt,
          ...adminFields
        } = payload.adminData;
        const filtered = Object.fromEntries(
          Object.entries(adminFields).filter(([key]) => !ADMIN_AUTO_POPULATE_OMIT.has(key))
        );
        next = { ...next, ...normalizeAdminDates(filtered) };
      }

      if (payload.intakeTrialSchemaUpdate) {
        next = {
          ...next,
          study_overview: {
            ...next.study_overview,
            ...payload.intakeTrialSchemaUpdate.study_overview,
          },
          study_treatments: {
            ...next.study_treatments,
            ...payload.intakeTrialSchemaUpdate.study_treatments,
          },
        };
      }

      return next;
    });

    if (payload.intakeTrialSchemaUpdate) {
      setTrialSchemaRevision((revision) => revision + 1);
    }

    if (payload.studyRegistrationData) {
      setStudyRegRefreshKey((k) => k + 1);
    }
  };

  const handleIntakeParsed = (parsedValues) => {
    if (!parsedValues || typeof parsedValues !== 'object') return;
    setFormData((prev) => ({
      ...prev,
      ...parsedValues,
      study_overview: {
        ...prev?.study_overview,
        ...parsedValues.study_overview,
      },
      study_treatments: {
        ...prev?.study_treatments,
        ...parsedValues.study_treatments,
      },
      study_identification: {
        ...prev?.study_identification,
        ...parsedValues.study_identification,
      },
      endpoints_objectives: {
        ...prev?.endpoints_objectives,
        ...parsedValues.endpoints_objectives,
      },
      target_population: {
        ...prev?.target_population,
        ...parsedValues.target_population,
      },
      background_information: {
        ...prev?.background_information,
        ...parsedValues.background_information,
      },
    }));
    setIntakeRevision((revision) => revision + 1);
  };

  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      setError(null);
      setIsViewMode(viewModeFromUrl);

      if (projectIdFromUrl) {
        setCurrentProjectId(projectIdFromUrl);
      }

      let combinedData = { ...initialFormData };

      // If we have a project, load latest Administrative Info (for Admin tab/title page)
      if (projectIdFromUrl) {
        try {
          const adminLatest = await administrativeInfoService.getLatestAdministrativeInfo(projectIdFromUrl);
          if (adminLatest?.data) {
            console.log('Loaded latest AdministrativeInfo for project:', projectIdFromUrl, adminLatest.data);
            combinedData = { ...combinedData, ...normalizeAdminDates(adminLatest.data) };
          } else {
            console.log('No AdministrativeInfo found for project:', projectIdFromUrl);
          }
        } catch (adminErr) {
          console.warn('Failed to fetch latest AdministrativeInfo:', adminErr);
        }
        try {
          const regRes = await getStudyRegistrationByProject(projectIdFromUrl);
          if (regRes?.data) {
            combinedData = { ...combinedData, ...studyRegistrationToAdminFields(regRes.data) };
          }
        } catch (regErr) {
          console.warn('Failed to fetch study registration:', regErr);
        }
      }

      if (formIdFromUrl && projectIdFromUrl) {
        try {
          const response = await clinicalIntakeService.getForm(formIdFromUrl, projectIdFromUrl);
          if (response.data) {
            //setFormData(response.data);
            // Merge clinical intake data with any admin info we loaded
            const merged = { ...combinedData, ...response.data };
            // AdministrativeInfo keys should not get overwritten by blank/partial values from Clinical Intake.
            // This preserves `AdministrativeInfoForm` fields like official_title/short_title.
            const preservedAdminKeys = [
              'trial_acronym',
              'official_title',
              'short_title',
              'sponsor_protocol_identifier',
              'original_protocol_indicator',
              'version_number',
              'version_date',
              'amendment_identifier',
              'amendment_scope',
              'sponsor_name',
              'sponsor_legal_address',
              'sponsor_entries',
              'co_sponsor_name',
              'co_sponsor_legal_address',
              'co_sponsor_entries',
              'collaborators',
              'collaborator_entries',
              'document_header',
              'sponsor_investigational_product_codes',
              'investigational_product_names',
              'local_sponsor_name',
              'local_sponsor_address',
              'local_sponsor_entries',
              'device_manufacturer_name',
              'device_manufacturer_address',
              'device_manufacturer_entries',
              'eu_ct_number',
              'fda_ind_number',
              'ide_number',
              'jrct_number',
              'nct_number',
              'nmpa_ind_number',
              'who_utn_number',
              'other_regulatory_identifier',
              'regulatory_identifier_entries',
              'sponsor_approval_date',
              'sponsor_approval_location',
              'sponsor_approval_location_text',
              'medical_expert_contact',
            ];

            const isNonEmpty = (v) => {
              if (v === undefined || v === null) return false;
              if (typeof v === 'string') return v.trim().length > 0;
              if (Array.isArray(v)) return v.length > 0;
              return true;
            };

            preservedAdminKeys.forEach((k) => {
              if (isNonEmpty(combinedData?.[k])) merged[k] = combinedData[k];
            });
            setFormData(merged);
            setActiveTab(0);
          } else {
            setError('Form data not found.');
            setFormData(combinedData);
          }
        } catch (err) {
          console.error('Failed to fetch clinical intake form:', err);
          setError(err.message || 'Failed to load form data.');
          setFormData(combinedData);
        }
      } else if (projectIdFromUrl) {
        console.log('🔧 First useEffect - Project ID found, loading only AdministrativeInfo and skipping activeTab logic (handled by second useEffect)');
        setFormData(combinedData);
        // Don't set activeTab here - second useEffect handles navigation
      } else {
        setFormData(initialFormData);
        setError('No project selected. Please select a project to create or view forms.');
      }
      setLoading(false);
    };

    loadFormData();
  }, [formIdFromUrl, projectIdFromUrl, viewModeFromUrl, tabFromUrl]);

  // Separate useEffect to handle tab parameter
  useEffect(() => {
    console.log('🔍 Tab URL Effect - tabFromUrl:', tabFromUrl, 'projectIdFromUrl:', projectIdFromUrl);
    // Map tabFromUrl to globalTab and subTab indices
    if (tabFromUrl && projectIdFromUrl) {
      // Protocol/SAP tabs only exist once their document is enabled in Study Setup —
      // jumping straight to their content when it isn't selected left the tab bar showing
      // no active tab while content rendered anyway. Land on Study Setup instead so the
      // user can enable the document first.
      const protocolReady = selectedDocs.has('Protocol');
      if (tabFromUrl === 'protocol') {
        if (protocolReady) {
          setGlobalTab(1);
          // A plain '?tab=protocol' deep link defaults to Study Registration, but when
          // '&protocolSubTab=<slug>' also rides along (written by the sub-tab Tabs'
          // onChange below so a reload lands back on the right sub-tab) that's more
          // specific — leave it alone here and let the protocolSubTabFromUrl effect
          // further down set the actual index, instead of racing it back to 0.
          if (!protocolSubTabFromUrl) {
            setProtocolSubTab(0); // Study Registration by default
          }
        } else {
          setGlobalTab(0); // Study Setup
        }
      } else if (tabFromUrl === 'document-editor') {
        if (protocolReady) {
          console.log('✅ Setting Document Editor: globalTab=1, protocolSubTab=7');
          setGlobalTab(1); // Protocol
          setProtocolSubTab(7); // Document Editor
        } else {
          setGlobalTab(0);
        }
      } else if (tabFromUrl === 'source-documents') {
        if (protocolReady) {
          setGlobalTab(1);
          setProtocolSubTab(1);
        } else {
          setGlobalTab(0);
        }
      } else if (tabFromUrl === 'administrative-info') {
        if (protocolReady) {
          setGlobalTab(1);
          setProtocolSubTab(2);
        } else {
          setGlobalTab(0);
        }
      } else if (tabFromUrl === 'clinical-intake') {
        if (protocolReady) {
          setGlobalTab(1);
          setProtocolSubTab(3);
        } else {
          setGlobalTab(0);
        }
      } else if (tabFromUrl === 'soa') {
        if (protocolReady) {
          setGlobalTab(1);
          setProtocolSubTab(4);
        } else {
          setGlobalTab(0);
        }
      } else if (tabFromUrl === 'template-language') {
        if (protocolReady) {
          setGlobalTab(1);
          setProtocolSubTab(5);
        } else {
          setGlobalTab(0);
        }
      } else if (tabFromUrl === 'sap') {
        if (selectedDocs.has('sap')) {
          setGlobalTab(2); // SAP
          setSapSubTab(DEFAULT_SAP_SUB_TAB); // first visible SAP sub-tab by default
        } else {
          setGlobalTab(0); // Study Setup
        }
      } else if (tabFromUrl === 'icf') {
        setGlobalTab(3); // ICF
        setSelectedDocs((prev) => {
          if (prev.has('ICF')) return prev;
          const next = new Set(prev);
          next.add('ICF');
          return next;
        });
      } else if (tabFromUrl === 'ind') {
        if (IND_ENABLED && selectedDocs.has('IND')) {
          setGlobalTab(10); // IND
        } else {
          setGlobalTab(0); // Study Setup
        }
      } else if (tabFromUrl === 'feasibility') {
        // Hidden (see HIDDEN_OTHER_DOC_IDS) — land on Study Setup instead
        setGlobalTab(isOtherDocHidden('feasibility') ? 0 : 4);
      } else if (tabFromUrl === 'registry-management') {
        if (isOtherDocHidden('registry-management')) {
          setGlobalTab(0); // Hidden (see HIDDEN_OTHER_DOC_IDS) — land on Study Setup instead
        } else {
          setGlobalTab(5); // Protocol Registration
          setRegistryManagementSubTab(0);
        }
      } else if (tabFromUrl === 'sops') {
        setGlobalTab(6); // SOPs
      } else if (tabFromUrl === 'translations') {
        setGlobalTab(7);
      } else if (tabFromUrl === 'design-studio') {
        setGlobalTab(8); // Design Studio
      } else if (tabFromUrl === 'templates') {
        setGlobalTab(9); // Templates
      } else if (tabFromUrl === 'home') {
        setGlobalTab(0); // Study Dashboard
      }
    }
  // selectedDocs is intentionally a dependency, not just read: `protocolReady`/
  // `selectedDocs.has('sap')` above are computed from it, but on first mount
  // (e.g. arriving here via a study card's `?tab=protocol` link) selectedDocs
  // starts from a possibly-stale/empty localStorage snapshot (see the effect
  // that loads it) while the real value is still an in-flight network request.
  // Without selectedDocs here, this effect ran once with that stale snapshot,
  // decided Protocol wasn't ready, and landed on Study Setup (globalTab 0) —
  // then never got a second chance to reconsider once the real selection
  // loaded, leaving the tab bar (driven independently by MainLayout.js's own
  // copy of the same data) showing "Protocol" selected while this component's
  // content stayed on Study Setup, until the user navigated away and back
  // (which changes location.search and re-runs this effect with fresh data).
  }, [tabFromUrl, projectIdFromUrl, location.search, selectedDocs]);

  useEffect(() => {
    if (!projectIdFromUrl || !protocolSubTabFromUrl) return;
    // 'protocolSubTab' is written alongside 'tab=protocol' (see handleProtocolSubTabChange
    // below) so it can survive a reload, but switching to a DIFFERENT top-level tab (e.g.
    // Registry Management, Home, SAP…) only overwrites 'tab' — it leaves the old
    // 'protocolSubTab' value sitting in the URL. Without this guard, that stale value kept
    // hijacking globalTab back to Protocol on every reload regardless of what 'tab' said,
    // because this effect never looked at tabFromUrl at all. Only act when the URL is
    // actually asking for Protocol (or omits 'tab' entirely, e.g. a hand-built link).
    if (tabFromUrl && tabFromUrl !== 'protocol') return;
    if (!selectedDocs.has('Protocol')) {
      setGlobalTab(0); // Protocol not enabled yet — land on Study Setup instead
      return;
    }
    const idx = protocolSubTabs.findIndex((t) => t.slug === protocolSubTabFromUrl);
    if (idx >= 0) {
      setGlobalTab(1);
      setProtocolSubTab(idx);
    }
  // selectedDocs is a real dependency, not just read: on a hard reload it starts from a
  // possibly-stale/empty snapshot (see the tabFromUrl effect above for the same race) —
  // without it here, a reload straight into '?protocolSubTab=usdm-template' could catch
  // that stale snapshot, decide Protocol wasn't enabled, and land on Study Setup for good.
  }, [protocolSubTabFromUrl, projectIdFromUrl, selectedDocs, tabFromUrl]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (mainTabs[newValue].label !== 'Document Editor') {
      setDocumentSubTab(0);
    }
  };

  // protocolSubTab previously lived only in local state — a reload always dropped it
  // back to Study Registration (index 0). Writing the slug into '?protocolSubTab=' lets
  // the effect above restore it; 'tab=protocol' stays so MainLayout's top nav keeps
  // "Protocol" highlighted (it reads its own copy of ?tab= for that), and the tabFromUrl
  // effect's 'protocol' branch now defers to protocolSubTabFromUrl instead of racing it.
  const handleProtocolSubTabChange = (_event, newValue) => {
    setProtocolSubTab(newValue);
    const slug = protocolSubTabs[newValue]?.slug;
    if (!slug) return;
    const params = new URLSearchParams(location.search);
    params.set('tab', 'protocol');
    params.set('protocolSubTab', slug);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };

  const handleDocumentSubTabChange = (event, newValue) => {
    setDocumentSubTab(newValue);
  };

  const handleSapSubTabChange = (event, newValue) => {
    setSapSubTab(newValue);
  };

  const handleFormSubmit = async (stepData, projectIdFromClinicalIntake) => {
    try {
      setLoading(true);
      setError(null);

      const updatedFormData = {
        ...formData,
        ...stepData
      };
      setFormData(updatedFormData);

      if (projectIdFromClinicalIntake) {
        setCurrentProjectId(projectIdFromClinicalIntake);
        setActiveTab(mainTabs.findIndex(tab => tab.label === 'Document Editor'));
      } else if (activeTab === mainTabs.length - 1) {
        // Tag the record with its study so StudyProtocol audit entries can be
        // scoped by study (see project field added to studyProtocol.model.js).
        const payload = currentProjectId ? { ...updatedFormData, project: currentProjectId } : updatedFormData;
        if (formIdFromUrl) {
          await studyProtocolService.updateStudyProtocol(formIdFromUrl, payload);
        } else {
          await studyProtocolService.createStudyProtocol(payload);
        }
        setSuccess(true);
        setTimeout(() => {
          navigate('/studies');
        }, 2000);
      } else if (globalTab === 0 && sapSubTab === 0) {
        // If submitting SAP Intake Form, go to SAP Editor sub-tab
        setSapSubTab(1);
      } else {
        setActiveTab(activeTab + 1);
      }
    } catch (err) {
      setError(err.message || 'Failed to save protocol');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = (tabIndex) => {
    const TabComponent = mainTabs[tabIndex].component;
    const isDocumentEditor = mainTabs[tabIndex].label === 'Document Editor';
    const isSAP = mainTabs[tabIndex].label === 'SAP';

    if (isDocumentEditor) {
      switch (documentSubTab) {
        case 0:
          return (
            <DocumentEditor
              onSubmit={handleFormSubmit}
              initialData={formData}
              loading={loading}
              projectId={currentProjectId}
            />
          );
        case 1:
          return (
            <ICFEditor
              onSubmit={handleFormSubmit}
              initialData={formData}
              loading={loading}
              projectId={currentProjectId}
              documentId={currentProjectId}
            />
          );
        case 2:
          return (
            <SAPEditor
              onSubmit={handleFormSubmit}
              initialData={formData}
              loading={loading}
            />
          );
        default:
          return <Typography>Select a document type</Typography>;
      }
    }
    if (isSAP) {
      switch (sapSubTab) {
        case 0:
          return (
            <OperationInputForm
              onSubmit={handleFormSubmit}
              initialData={formData || {}}
              loading={loading}
              onSuccess={() => setSapSubTab(1)}
            />
          );
        case 1:
          return (
            <SAPEditor
              onSubmit={handleFormSubmit}
              initialData={formData || {}}
              loading={loading}
            />
          );
        case 2:
          return (
            <SAPEditorUsdm
              onSubmit={handleFormSubmit}
              initialData={formData || {}}
              loading={loading}
            />
          );
        default:
          return <Typography>Select a SAP tab</Typography>;
      }
    }
    if (TabComponent === DocumentMap) {
      return (
        <DocumentMap
          onSubmit={handleFormSubmit}
          initialData={formData}
          loading={loading}
          isViewMode={isViewMode}
          projectId={currentProjectId}
        />
      );
    }
    return (
      <TabComponent
        onSubmit={handleFormSubmit}
        initialData={formData}
        loading={loading}
        isViewMode={isViewMode}
      />
    );
  };

  if (projectAccessDenied) {
    return <PolicyDeniedPane reason={projectDeniedReason} size="wide" />;
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ p: 0 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mt: 2, mb: 4, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto', overflowY: 'visible' }}>
        {/* Global tabs (Study Dashboard/Protocol/SAP/ICF/...) now live in the top app bar next to the logo (MainLayout.js) */}
        {/* Study Dashboard tab: status overview + Study Setup, merged into one view */}
        {globalTab === 0 ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : homeTabGate.showDenied ? (
            <PolicyDeniedPane reason={homeTabGate.deniedMessage} size="fill" />
          ) : (
          <Box sx={{ py: 3, px: 2, minHeight: '60vh', backgroundColor: 'background.paper', borderRadius: 2 }}>
            {docSelectionWarning && (
              <Alert severity="warning" onClose={() => setDocSelectionWarning('')} sx={{ mb: 2 }}>
                {docSelectionWarning}
              </Alert>
            )}
            <HomeStudyDesignTab
              selectedDocs={selectedDocs}
              onDocToggle={handleDocToggle}
              policiesLoaded={policiesLoaded}
              probeSignature={policyStudyScopeId}
            />
            <Divider sx={{ my: 4 }} />
            <StudyDashboard />
          </Box>
          )
        ) : globalTab === 2 ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : sapTabGate.showDenied ? (
            <PolicyDeniedPane reason={sapTabGate.deniedMessage} size="fill" />
          ) : (
          <Box>
            <Tabs
              value={sapSubTab}
              onChange={handleSapSubTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="sap sub-tabs"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: '2px 2px 0 0',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 100,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  py: 1,
                  px: 2,
                  mx: 0.5,
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(30, 115, 190, 0.04)',
                    color: 'primary.main',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(30, 115, 190, 0.02)',
                  },
                },
              }}
            >
              {visibleSubTabsSAP.map((subTab) => (
                <Tab key={subTab.label} label={subTab.label} value={subTab.value} />
              ))}
            </Tabs>
            <Box sx={{ py: 3, px: 2, minHeight: '60vh', backgroundColor: 'background.paper', borderRadius: '0 0 8px 8px' }}>
              {sapSubTab === 0 && (
                <OperationInputForm
                  onSubmit={handleFormSubmit}
                  initialData={formData || {}}
                  loading={loading}
                  onSuccess={() => setSapSubTab(1)}
                />
              )}
              {sapSubTab === 1 && (
                <SAPEditor
                  onSubmit={handleFormSubmit}
                  initialData={formData || {}}
                  loading={loading}
                />
              )}
              {sapSubTab === 2 && (
                <SAPEditorUsdm
                  onSubmit={handleFormSubmit}
                  initialData={formData || {}}
                  loading={loading}
                />
              )}
            </Box>
          </Box>
          )
        ) : globalTab === 1 ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : protocolTabGate.showDenied ? (
            <PolicyDeniedPane reason={protocolTabGate.deniedMessage} size="fill" />
          ) : (
          <Box>
            {console.log('📊 Rendering Protocol Tab - globalTab:', globalTab, 'protocolSubTab:', protocolSubTab)}
            <Tabs
              value={selectedProtocolSubTab}
              onChange={handleProtocolSubTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="protocol sub-tabs"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: '2px 2px 0 0',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 100,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  py: 1,
                  px: 2,
                  mx: 0.5,
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(30, 115, 190, 0.04)',
                    color: 'primary.main',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(30, 115, 190, 0.02)',
                  },
                },
              }}
            >
              {visibleProtocolSubTabs.map((idx) => (
                <Tab key={protocolSubTabs[idx].label} label={protocolSubTabs[idx].label} value={idx} />
              ))}
            </Tabs>
            <Box
              sx={{
                py: protocolSubTab === 4 ? 1.5 : 3,
                px: protocolSubTab === 1 ? 1 : protocolSubTab === 4 ? 1.5 : 2,
                minHeight: protocolSubTab === 4 ? 'calc(100vh - 200px)' : '60vh',
                backgroundColor: 'background.paper',
                borderRadius: '0 0 8px 8px',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: protocolSubTab === 1 ? 'auto' : 'hidden',
                ...(protocolSubTab === 4
                  ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' }
                  : {}),
              }}
            >
              {protocolSubTab === 0 && (
                <StudyRegistrationForm
                  key={studyRegRefreshKey}
                  projectId={currentProjectId}
                  onMergedToFormData={(partial) => {
                    setFormData((prev) => ({ ...prev, ...partial }));
                  }}
                />
              )}
              {protocolSubTab === 1 && (
                <SourceDocumentsForm
                  onSubmit={handleFormSubmit}
                  initialData={formData || {}}
                  loading={loading}
                  isViewMode={isViewMode}
                  onAdminDataPopulated={handleAutoPopulateResult}
                />
              )}
              {protocolSubTab === 2 && (
                <AdministrativeInfoForm
                  onSubmit={handleFormSubmit}
                  initialData={formData || {}}
                  loading={loading}
                  projectId={currentProjectId}
                />
              )}
              {protocolSubTab === 3 && (
                <ClinicalIntakeForm
                  key={`clinical-intake-${trialSchemaRevision}-${intakeRevision}`}
                  onSubmit={handleFormSubmit}
                  initialData={formData || {}}
                  loading={loading}
                  hideDocuments
                  onIntakeParsed={handleIntakeParsed}
                />
              )}
              {protocolSubTab === 4 && (
                <S9AssessmentScheduleForm
                  projectId={currentProjectId}
                  initialData={formData || {}}
                />
              )}
              {protocolSubTab === 5 && (
                <TemplateLanguageForm
                  onSubmit={handleFormSubmit}
                  initialData={formData || {}}
                  loading={loading}
                  projectId={currentProjectId}
                />
              )}
              {protocolSubTab === 6 && (
                <UsdmTemplateTab
                  projectId={currentProjectId}
                  onViewInEditor={() => handleProtocolSubTabChange(null, 7)}
                  onGenerateProtocol={() => {
                    setAutoGenerateFromUsdm(true);
                    handleProtocolSubTabChange(null, 7);
                  }}
                />
              )}
              {protocolSubTab === 7 && (
                <DocumentEditor
                  onSubmit={handleFormSubmit}
                  initialData={formData || {}}
                  loading={loading}
                  autoGenerateFromUsdm={autoGenerateFromUsdm}
                  onAutoGenerateFromUsdmConsumed={() => setAutoGenerateFromUsdm(false)}
                />
              )}
            </Box>
          </Box>
          )
        ) : globalTab === 3 ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : icfTabGate.showDenied ? (
            <PolicyDeniedPane reason={icfTabGate.deniedMessage} size="fill" />
          ) : (
          <Box sx={{ py: 3, px: 2, minHeight: '60vh', backgroundColor: 'background.paper', borderRadius: '0 0 8px 8px' }}>
            <ICFEditor
              onSubmit={handleFormSubmit}
              initialData={formData}
              loading={loading}
              projectId={currentProjectId}
              documentId={currentProjectId}
            />
          </Box>
          )
        ) : globalTab === 4 && !isOtherDocHidden('feasibility') ? (
          <Box sx={{ py: 3, px: 2, minHeight: '60vh', backgroundColor: 'background.paper', borderRadius: '0 0 8px 8px' }}>
            <Feasibility />
          </Box>
        ) : globalTab === 5 && !isOtherDocHidden('registry-management') ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : registryManagementTabGate.showDenied ? (
            <PolicyDeniedPane reason={registryManagementTabGate.deniedMessage} size="fill" />
          ) : (
          <RegistryManagement projectId={currentProjectId} />
          )
        ) : globalTab === 6 ? (
          <SOPsPage projectId={currentProjectId} />
        ) : globalTab === 7 ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : translationsTabGate.showDenied ? (
            <PolicyDeniedPane reason={translationsTabGate.deniedMessage} size="fill" />
          ) : (
          <TranslationsPage projectId={currentProjectId} />
          )
        ) : globalTab === 8 ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : designStudioTabGate.showDenied ? (
            <PolicyDeniedPane reason={designStudioTabGate.deniedMessage} size="fill" />
          ) : (
          <DesignStudioPage projectId={currentProjectId} />
          )
        ) : globalTab === 9 ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : libraryTabGate.showDenied ? (
            <PolicyDeniedPane reason={libraryTabGate.deniedMessage} size="fill" />
          ) : (
          <TemplatesTab projectId={currentProjectId} />
          )
        ) : globalTab === 10 && IND_ENABLED ? (
          !policiesLoaded ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : indTabGate.showDenied ? (
            <PolicyDeniedPane reason={indTabGate.deniedMessage} size="fill" />
          ) : (
          <Box sx={{ py: 3, px: 2, minHeight: '60vh', backgroundColor: 'background.paper', borderRadius: '0 0 8px 8px' }}>
            <INDPage />
          </Box>
          )
        ) : (
          // Otherwise, show the mainTabs as before
          <>
      
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="protocol form tabs"
            sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 12px 0 rgba(30, 115, 190, 0.10)',
                  background: 'linear-gradient(90deg, #e3f2fd 60%, #fff 100%)',
                  mb: 3,
                  p: 1,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 200,
                fontSize: '1rem',
                fontWeight: 500,
                py: 2,
                px: 3,
                mx: 1,
                borderRadius: '8px 8px 0 0',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(30, 115, 190, 0.08)',
                  color: 'primary.main',
                },
                '&:hover': {
                  backgroundColor: 'rgba(30, 115, 190, 0.04)',
                },
              },
            }}
          >
            {mainTabs.map((tab, index) => (
              <Tab 
                key={tab.label} 
                label={
                  <Typography variant="subtitle1" sx={{ fontWeight: activeTab === index ? 600 : 500 }}>
                    {tab.label}
                  </Typography>
                }
                id={`protocol-tab-${index}`}
                aria-controls={`protocol-tabpanel-${index}`}
              />
            ))}
          </Tabs>
          {activeTab === mainTabs.findIndex(tab => tab.label === 'Document Editor') && (
            <Tabs
              value={documentSubTab}
              onChange={handleDocumentSubTabChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="document editor sub-tabs"
              sx={{
                mt: 1,
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTabs-indicator': {
                  height: 2,
                  borderRadius: '2px 2px 0 0',
                },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 100,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  py: 1,
                  px: 2,
                  mx: 0.5,
                  borderRadius: '4px 4px 0 0',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(30, 115, 190, 0.04)',
                    color: 'primary.main',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(30, 115, 190, 0.02)',
                  },
                },
              }}
            >
              {subTabsDocumentEditor.map((subTab) => (
                <Tab key={subTab.label} label={subTab.label} value={subTab.value} />
              ))}
            </Tabs>
          )}
        </Box>
        {mainTabs.map((tab, index) => (
          <Box
            key={tab.label}
            role="tabpanel"
            hidden={activeTab !== index}
            id={`protocol-tabpanel-${index}`}
            aria-labelledby={`protocol-tab-${index}`}
            sx={{
              py: 3,
              px: 2,
              minHeight: '60vh',
              backgroundColor: 'background.paper',
              borderRadius: '0 0 8px 8px',
            }}
          >
            {activeTab === index && renderTabContent(index)}
          </Box>
        ))}
          </>
        )}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mt: 4,
          pt: 3,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          {/* <Button
            disabled={activeTab === 0 || loading}
            onClick={() => setActiveTab(activeTab - 1)}
            variant="outlined"
            sx={{ 
              minWidth: 120,
              py: 1.5,
              px: 4,
              borderRadius: 2
            }}
          >
            
          </Button> */}
          {/* <Button
            variant="contained"
            color="primary"
            onClick={() => handleFormSubmit(formData)}
            disabled={loading}
            sx={{ 
              minWidth: 120,
              py: 1.5,
              px: 4,
              borderRadius: 2
            }}
          >
            
          </Button> */}
        </Box>




        <Snackbar
          open={error !== null}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
        >
          <Alert severity="success" onClose={() => setSuccess(false)}>
            Protocol {formIdFromUrl ? 'updated' : 'created'} successfully!
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
};

export default StudyFormTabs; 